import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Button, Icon } from "./souvenir.ts";
import { WAVE_BACK, WAVE_FRONT } from "./ui.tsx";

// How Tripmo works (features/getting-started.md, "How Tripmo works, first"): four short screens, one
// idea each, each with a small looping drawing in Souvenir's hand (ink outlines, warm fills, the map's
// paper and sea), and Next. Someone new sees them once, before connecting their AI; the connect screen
// and the profile show them again. "I know how it works" skips to the end from any screen. Under Reduce
// Motion each drawing stands still, finished.

interface Screen {
  title: string;
  text: string;
  art: () => ReactNode;
}

const SCREENS: Screen[] = [
  {
    title: "Plan by talking",
    text: "Tell your AI what you want, in your own words. It does the planning: finds places, compares hotels, keeps your bookings.",
    art: () => <TalkArt />,
  },
  {
    title: "Tripmo keeps the plan",
    text: "Everything your AI saves shows up here: the map, your days, bookings, what's still to decide. Rate ideas, tick off to-dos and mark things booked right here; your AI does the rest.",
    art: () => <KeepArt />,
  },
  {
    title: "Everyone's AI, one plan",
    text: "Friends join with a link and plan with their own AI, Claude, ChatGPT or another, into the same trip.",
    art: () => <TogetherArt />,
  },
  {
    title: "One thing to set up",
    text: "Your AI needs a way into your Tripmo plan, like adding an app to it. It's called a connector (MCP). It takes a minute.",
    art: () => <ConnectArt />,
  },
];

// `last`: what the last screen's button says (on to connecting, or back where it was opened from);
// `onDone`: the last screen's button and "I know how it works". `note`: a line above the first screen
// (from a join link: that they've asked).
export function HowItWorks({ last, onDone, note }: { last: string; onDone: () => void; note?: string }) {
  const [at, setAt] = useState(0);
  const screen = SCREENS[at] as Screen;
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const before = document.title;
    document.title = "How Tripmo works · Tripmo";
    return () => {
      document.title = before;
    };
  }, []);
  // Each new screen is read from its title, as a new page would be (not the first: the page just opened).
  const shown = useRef(0);
  useEffect(() => {
    if (at === shown.current) return;
    shown.current = at;
    heading.current?.focus();
  }, [at]);
  const final = at === SCREENS.length - 1;
  return (
    <main className="tm-how">
      {note && at === 0 && (
        <p className="callout tm-how-note" role="status">
          <Icon name="check" size={18} className="tm-how-note-icon" />
          <span>{note}</span>
        </p>
      )}
      <div className="tm-how-art" key={at} aria-hidden="true">
        {screen.art()}
      </div>
      <p className="tm-how-dots">
        {SCREENS.map((s, i) => (
          <span key={s.title} className={`tm-how-dot${i === at ? " is-on" : ""}`} />
        ))}
        <span className="tm-hidden">
          {at + 1} of {SCREENS.length}
        </span>
      </p>
      <h1 className="display-l tm-how-title" ref={heading} tabIndex={-1}>
        {screen.title}
      </h1>
      <p className="body tm-how-text">{screen.text}</p>
      <div className="tm-how-foot">
        <Button variant="primary" block onClick={() => (final ? onDone() : setAt(at + 1))}>
          {final ? last : "Next"}
        </Button>
        {final ? (
          // Where "I know how it works" was, so the last button stays where the finger is.
          <span className="tm-how-skip-space" aria-hidden="true" />
        ) : (
          <Button variant="quiet" block onClick={onDone}>
            I know how it works
          </Button>
        )}
      </div>
    </main>
  );
}

// ---------- The drawings ----------
// Each is 320 by 180, drawn with Souvenir's tokens (app.css, "How Tripmo works"), on one 6-second loop:
// an element arrives at its slot (`tm-in-N`, N from 0 to 7) and everything leaves together at the end.
// Their resting style is the finished drawing, which is what Reduce Motion shows.

export const PIN = "M0 0C-2.6-5.4-9-8.6-9-14.6a9 9 0 1 1 18 0C9-8.6 2.6-5.4 0 0Z";

// A map pin in a person's colour, its point at (x, y).
function Pin({ x, y, color, slot }: { x: number; y: number; color: string; slot: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className={`a-pin tm-in-${slot}`}>
        <path className={`a-pin-head a-fill-${color}`} d={PIN} />
        <circle className="a-pin-eye" cx="0" cy="-14.6" r="3.2" />
      </g>
    </g>
  );
}

// Land on the sea, inside a rounded frame: the trip map in miniature, in the base map's colours.
function MiniMap({ x, y, w, h, children }: { x: number; y: number; w: number; h: number; children?: ReactNode }) {
  const clip = useId();
  return (
    <g>
      <defs>
        <clipPath id={clip}>
          <rect x={x} y={y} width={w} height={h} rx="14" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        <rect className="a-sea" x={x} y={y} width={w} height={h} />
        <path
          className="a-land"
          d={`M${x - 4} ${y + h * 0.18}C${x + w * 0.3} ${y + h * 0.05} ${x + w * 0.55} ${y + h * 0.3} ${x + w * 0.8} ${y + h * 0.22}S${x + w + 6} ${y + h * 0.3} ${x + w + 6} ${y + h * 0.4}V${y + h + 6}H${x + w * 0.62}C${x + w * 0.5} ${y + h * 0.82} ${x + w * 0.25} ${y + h * 0.95} ${x - 4} ${y + h * 0.78}Z`}
        />
        {children}
      </g>
      <rect className="a-frame" x={x} y={y} width={w} height={h} rx="14" />
    </g>
  );
}

// A dashed route that draws itself from its start (a mask follows the solid line along it).
function Route({ d, slot, className = "a-route" }: { d: string; slot: number; className?: string }) {
  const mask = useId();
  return (
    <g>
      <defs>
        <mask id={mask} maskUnits="userSpaceOnUse">
          <path className={`a-reveal tm-draw-${slot}`} d={d} pathLength={1} />
        </mask>
      </defs>
      <path className={className} d={d} mask={`url(#${mask})`} />
    </g>
  );
}

// A chat bubble, its tail at the bottom left, with lines of text (and the face of whoever's talking).
function Bubble({
  x,
  y,
  w,
  h,
  lines,
  avatar,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  lines: { w: number; slot: number }[];
  avatar?: string;
}) {
  const r = 14;
  const d = `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + 28}L${x + 14} ${y + h + 10}L${x + 17} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;
  const left = avatar ? x + 40 : x + 16;
  const top = y + h / 2 - (lines.length * 13 - 7) / 2;
  return (
    <g>
      <path className="a-bubble" d={d} />
      {avatar && <circle className={`a-fill-${avatar} a-avatar`} cx={x + 21} cy={y + h / 2} r="9" />}
      {lines.map((line, i) => (
        <rect
          // biome-ignore lint/suspicious/noArrayIndexKey: a bubble's lines, in order
          key={i}
          className={`a-line tm-in-${line.slot}`}
          x={left}
          y={top + i * 13}
          width={line.w}
          height="6"
          rx="3"
        />
      ))}
    </g>
  );
}

function TalkArt() {
  return (
    <svg className="tm-art" viewBox="0 0 320 180" role="presentation">
      <Bubble
        x={12}
        y={38}
        w={142}
        h={72}
        avatar="amber"
        lines={[
          { w: 84, slot: 0 },
          { w: 70, slot: 1 },
          { w: 46, slot: 2 },
        ]}
      />
      <path className="a-hint tm-in-3" d="M161 66C171 62 178 66 184 74" />
      <MiniMap x={192} y={22} w={114} h={140}>
        <Route d="M220 108C232 92 246 96 254 78S276 64 284 70" slot={7} />
        <Pin x={220} y={108} color="sun" slot={4} />
        <Pin x={254} y={78} color="coral" slot={5} />
        <Pin x={284} y={70} color="sea" slot={6} />
      </MiniMap>
    </svg>
  );
}

function KeepArt() {
  return (
    <svg className="tm-art" viewBox="0 0 320 180" role="presentation">
      <rect className="a-card" x={44} y={10} width={232} height={160} rx="20" />
      <MiniMap x={58} y={24} w={204} h={82}>
        <Route d="M96 82C120 62 142 74 160 56S206 46 226 60" slot={4} />
        <circle className="a-dot a-fill-sea tm-in-0" cx="96" cy="82" r="5.5" />
        <circle className="a-dot a-fill-coral tm-in-1" cx="160" cy="56" r="5.5" />
        <circle className="a-dot a-fill-sun tm-in-2" cx="226" cy="60" r="5.5" />
        <circle className="a-dot a-fill-moss tm-in-3" cx="132" cy="92" r="5.5" />
      </MiniMap>
      {/* The three ratings as everywhere, No, Maybe, Yes, left to right (getting-started.md, How Tripmo
          works, screen 2), 8 apart and as wide together as the map over them, each its words (11 px: 14.9,
          33.9 and 17.3 wide) and an even share of the rest, as the buttons share a row (Rate.tsx). The card
          is 232 wide since "Not interested" needed it (2026-10-22 to 23; 208 before, with Must, Want, Maybe).
          A finger taps Yes (Must until 2026-10-23). */}
      {CHIPS.map((chip) => (
        <g key={chip.label}>
          <rect
            className={`a-chip${chip.label === "Yes" ? " a-yes" : ""}`}
            x={chip.x}
            y={120}
            width={chip.w}
            height={26}
            rx="13"
          />
          <text
            className={`a-chip-text${chip.label === "Yes" ? " a-yes" : ""}`}
            x={chip.x + chip.w / 2}
            y={137}
            textAnchor="middle"
          >
            {chip.label}
          </text>
        </g>
      ))}
      <g transform={`translate(${YES.x + YES.w / 2 - 103.5} 6)`}>
        <g className="a-finger">
          <circle className="a-tap" cx="103.5" cy="131" r="13" />
          <path
            className="a-hand"
            d="M100 150v-15.5a3.5 3.5 0 0 1 7 0V146l1.5-1a3.5 3.5 0 0 1 4.6 5.2l-6.2 8.3a8 8 0 0 1-6.4 3.2h-1.2a7 7 0 0 1-6.5-4.4l-2.3-5.8a2.6 2.6 0 0 1 4.6-2.4Z"
          />
        </g>
      </g>
    </svg>
  );
}

// Screen 2's ratings, under its map (x 58 to 262).
const CHIPS = [
  { x: 58, w: 55.5, label: "No" },
  { x: 121.5, w: 74.5, label: "Maybe" },
  { x: 204, w: 58, label: "Yes" },
] as const;
const YES = CHIPS[2];

function TogetherArt() {
  return (
    <svg className="tm-art" viewBox="0 0 320 180" role="presentation">
      <Bubble
        x={12}
        y={16}
        w={128}
        h={60}
        avatar="teal"
        lines={[
          { w: 70, slot: 0 },
          { w: 50, slot: 1 },
        ]}
      />
      <Bubble
        x={12}
        y={100}
        w={128}
        h={60}
        avatar="coral"
        lines={[
          { w: 62, slot: 2 },
          { w: 74, slot: 3 },
        ]}
      />
      <path className="a-flow a-stroke-teal" d="M144 46C170 46 172 74 196 76" />
      <path className="a-flow a-stroke-coral" d="M144 130C170 130 172 104 196 102" />
      <MiniMap x={196} y={22} w={112} h={136}>
        <Pin x={226} y={84} color="teal" slot={4} />
        <Pin x={276} y={70} color="coral" slot={5} />
        <Pin x={252} y={122} color="teal" slot={6} />
        <Pin x={286} y={128} color="coral" slot={7} />
      </MiniMap>
    </svg>
  );
}

function ConnectArt() {
  return (
    <svg className="tm-art" viewBox="0 0 320 180" role="presentation">
      <g className="a-plug-in">
        <path className="a-cable" d="M80 82H146" />
        <rect className="a-plug" x={146} y={72} width={24} height={20} rx="5" />
        <path className="a-prong" d="M170 77.5h12M170 86.5h12" />
      </g>
      <Bubble
        x={16}
        y={50}
        w={96}
        h={64}
        lines={[
          { w: 62, slot: 0 },
          { w: 44, slot: 1 },
        ]}
      />
      <rect className="a-plug" x={168} y={68} width={34} height={28} rx="6" />
      <rect className="a-card" x={196} y={44} width={100} height={76} rx="18" />
      <g transform="translate(222 55) scale(0.75)">
        <MarkArt />
      </g>
      <g className="tm-in-4 a-done">
        <circle className="a-check-disc" cx="170" cy="46" r="13" />
        <path className="a-check" d="M164 46.5l4 4 8-8.5" />
      </g>
    </svg>
  );
}

// Tripmo's mark (ui.tsx's Mark), drawn into a drawing at 64 by 64.
function MarkArt() {
  const mask = useId();
  return (
    <g className="a-mark">
      <defs>
        <mask id={mask} maskUnits="userSpaceOnUse">
          <rect width="64" height="64" fill="#fff" />
          <path d={WAVE_FRONT} fill="none" stroke="#000" strokeWidth="12.5" strokeLinecap="round" />
        </mask>
      </defs>
      <circle cx="37" cy="30" r="14.5" className="a-fill-sun" mask={`url(#${mask})`} />
      <path d={WAVE_FRONT} fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      <path d={WAVE_BACK} fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
    </g>
  );
}
